// Backoffice — Checklists Fase 1
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import checklistService from "../../services/checklistService";
import api from "../../services/api";
import { getApiBaseUrl } from "../../services/apiBase";
import Banner from "../../components/ui/Banner";

const fmtDT = (d) => {
  try { return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return d || "—"; }
};
const fmtDTFull = (d) => {
  try {
    return new Date(d).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return d || "—"; }
};
const fmtDuracao = (seg) => {
  const s = Number(seg);
  if (!Number.isFinite(s) || s < 0) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min ${sec > 0 ? `${sec}s` : ""}`.trim();
  return `${sec}s`;
};
const parseFotos = (fotos) => {
  if (!fotos) return [];
  if (Array.isArray(fotos)) return fotos;
  try {
    const arr = JSON.parse(fotos);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};
const urlFoto = (path) => {
  if (!path) return "";
  if (String(path).startsWith("http")) return path;
  const base = getApiBaseUrl().replace(/\/api$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};
const duracaoExecucao = (ex) => {
  if (!ex) return "—";
  if (ex.duracao_segundos != null && ex.duracao_segundos !== "") {
    return fmtDuracao(ex.duracao_segundos);
  }
  if (ex.dtinicio && ex.dtfim) {
    const s = Math.round((new Date(ex.dtfim) - new Date(ex.dtinicio)) / 1000);
    return fmtDuracao(s);
  }
  if (ex.dtinicio && ex.status === "EmAndamento") {
    const s = Math.round((Date.now() - new Date(ex.dtinicio)) / 1000);
    return `${fmtDuracao(s)} (em andamento)`;
  }
  return "—";
};
const fmtD = (d) => {
  try { const s = (d || "").split("T")[0]; return s ? new Date(`${s}T12:00:00`).toLocaleDateString("pt-BR") : "—"; }
  catch { return d || "—"; }
};
const today = () => new Date().toISOString().slice(0, 10);

const TP_LABEL = { SimNao: "Sim/Não", Texto: "Texto", Numero: "Número", Opcoes: "Opções", Foto: "Foto" };
const ST_BADGE = { Pendente: "warning", EmAndamento: "info", Atrasada: "danger", Concluida: "success", Cancelada: "secondary" };

const ITEM_EMPTY = { pergunta: "", tpresposta: "SimNao", opcoes: "", obrigatorio: true, geranc: false };
const MOD_EMPTY = { nmmodelo: "", descricao: "", categoria: "Outro", ativo: true, itens: [{ ...ITEM_EMPTY }] };
const AG_EMPTY = {
  nmrotina: "", idmodelo: "", recorrencia: "Diaria", diasemana: "1,2,3,4,5",
  hrinicio: "08:00", hrfim: "18:00", idresponsavel: "", nmlocal: "", perderseatrasar: true, ativo: true,
};
const PA_EMPTY = {
  idResposta: "",
  oque: "",
  porque: "",
  onde: "",
  quandoData: "",
  quemIdUsuario: "",
  como: "",
  quanto: "",
  prioridade: "Media",
};

export default function ChecklistsView() {
  const { condominioAtivo, idCondoAtivo, podeEditar } = useAuth();
  const idCondominio = condominioAtivo?.id || idCondoAtivo || null;
  const canEdit = podeEditar?.("checklists") !== false;

  const [aba, setAba] = useState("dashboard");
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({ categorias: [], tpResposta: [] });

  const [kpi, setKpi] = useState({});
  const [modelos, setModelos] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [execucoes, setExecucoes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [modalMod, setModalMod] = useState(null);
  const [formMod, setFormMod] = useState(MOD_EMPTY);
  const [modalAg, setModalAg] = useState(null);
  const [formAg, setFormAg] = useState(AG_EMPTY);
  const [detExec, setDetExec] = useState(null);
  const [fotosViewer, setFotosViewer] = useState(null);
  const [formPA, setFormPA] = useState(PA_EMPTY);
  const [filtroStatusTarefa, setFiltroStatusTarefa] = useState("");
  const [filtroResponsavelTarefa, setFiltroResponsavelTarefa] = useState("");
  const [buscaModelo, setBuscaModelo] = useState("");
  const [buscaModeloDebounced, setBuscaModeloDebounced] = useState("");

  const notify = (type, msg) => setBanner({ type, msg });

  useEffect(() => {
    const t = setTimeout(() => setBuscaModeloDebounced(buscaModelo.trim()), 400);
    return () => clearTimeout(t);
  }, [buscaModelo]);

  useEffect(() => {
    checklistService.meta().then((r) => setMeta(r)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!idCondominio) return;
    api("GET", `/usuarios?idCondominio=${idCondominio}&limit=200&ativo=1`)
      .then((r) => setUsuarios(r.data || []))
      .catch(() => {});
  }, [idCondominio]);

  const carregar = useCallback(async () => {
    if (!idCondominio) return;
    setLoading(true);
    try {
      if (aba === "dashboard") {
        const r = await checklistService.dashboard({ idCondominio, data: today() });
        setKpi(r.kpi || {});
      } else if (aba === "modelos") {
        const r = await checklistService.listarModelos({
          idCondominio,
          search: buscaModeloDebounced || undefined,
        });
        setModelos(r.data || []);
      } else if (aba === "agendamentos") {
        const [a, m] = await Promise.all([
          checklistService.listarAgendamentos({ idCondominio }),
          checklistService.listarModelos({ idCondominio, ativo: 1 }),
        ]);
        setAgendamentos(a.data || []);
        setModelos(m.data || []);
      } else if (aba === "tarefas") {
        const r = await checklistService.listarTarefas({
          idCondominio,
          data: today(),
          status: filtroStatusTarefa || undefined,
          idResponsavel: filtroResponsavelTarefa || undefined,
        });
        setTarefas(r.data || []);
      } else if (aba === "execucoes") {
        const r = await checklistService.listarExecucoes({ idCondominio });
        setExecucoes(r.data || []);
      }
    } catch (e) {
      notify("danger", e.message || "Erro ao carregar.");
    } finally { setLoading(false); }
  }, [aba, idCondominio, filtroStatusTarefa, filtroResponsavelTarefa, buscaModeloDebounced]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirModelo = async (id) => {
    if (id) {
      const r = await checklistService.buscarModelo(id);
      setFormMod({
        ...r.modelo,
        nmmodelo: r.modelo.nmmodelo,
        itens: (r.itens || []).length ? r.itens.map((it) => ({
          ...it, obrigatorio: it.obrigatorio === 1, geranc: it.geranc === 1,
        })) : [{ ...ITEM_EMPTY }],
      });
      setModalMod(id);
    } else {
      setFormMod({ ...MOD_EMPTY, itens: [{ ...ITEM_EMPTY }] });
      setModalMod("new");
    }
  };

  const salvarModelo = async () => {
    try {
      const payload = {
        idCondominio,
        nmmodelo: formMod.nmmodelo,
        descricao: formMod.descricao,
        categoria: formMod.categoria,
        ativo: formMod.ativo ? 1 : 0,
        itens: formMod.itens.filter((i) => i.pergunta?.trim()),
      };
      if (modalMod === "new") await checklistService.criarModelo(payload);
      else await checklistService.atualizarModelo(modalMod, payload);
      setModalMod(null);
      notify("success", "Modelo salvo.");
      carregar();
    } catch (e) { notify("danger", e.message); }
  };

  const abrirAg = (ag) => {
    if (ag) {
      setFormAg({
        ...ag,
        idresponsavel: ag.idresponsavel || "",
        perderseatrasar: ag.perderseatrasar === 1,
        ativo: ag.ativo === 1,
      });
      setModalAg(ag.id);
    } else {
      setFormAg({ ...AG_EMPTY, idmodelo: modelos[0]?.id || "" });
      setModalAg("new");
    }
  };

  const salvarAg = async () => {
    try {
      const payload = {
        idCondominio,
        ...formAg,
        idresponsavel: formAg.idresponsavel || null,
        perderseatrasar: formAg.perderseatrasar ? 1 : 0,
        ativo: formAg.ativo ? 1 : 0,
      };
      if (modalAg === "new") await checklistService.criarAgendamento(payload);
      else await checklistService.atualizarAgendamento(modalAg, payload);
      setModalAg(null);
      notify("success", "Agendamento salvo.");
      carregar();
    } catch (e) { notify("danger", e.message); }
  };

  const verExec = async (id) => {
    try {
      const r = await checklistService.buscarExecucao(id);
      setDetExec(r);
      setFormPA(PA_EMPTY);
    } catch (e) { notify("danger", e.message); }
  };

  const criarPlanoAcao = async () => {
    try {
      if (!detExec?.execucao?.id) return;
      await checklistService.criarPlanoAcao(detExec.execucao.id, {
        ...formPA,
        quemIdUsuario: formPA.quemIdUsuario || null,
        idResposta: formPA.idResposta || null,
        quanto: formPA.quanto !== "" ? Number(formPA.quanto) : null,
      });
      const atualizado = await checklistService.buscarExecucao(detExec.execucao.id);
      setDetExec(atualizado);
      setFormPA(PA_EMPTY);
      notify("success", "Plano de ação criado.");
    } catch (e) { notify("danger", e.message); }
  };

  const imprimirDetalhe = async () => {
    if (!detExec?.execucao?.id) return;
    try {
      const r = await checklistService.imprimirExecucao(detExec.execucao.id);
      if (!r?.html) throw new Error("Relatório vazio.");
      const w = window.open("", "_blank", "width=900,height=900");
      if (!w) {
        notify("warning", "Pop-up bloqueado. Permita pop-ups deste site para imprimir.");
        return;
      }
      w.document.write(r.html);
      w.document.close();
    } catch (e) { notify("danger", e.message || "Erro ao gerar PDF."); }
  };

  const abrirOcorrencia = async (idResposta) => {
    try {
      if (!detExec?.execucao?.id) return;
      const r = await checklistService.criarOcorrenciaDaExecucao(detExec.execucao.id, { idResposta });
      const atualizado = await checklistService.buscarExecucao(detExec.execucao.id);
      setDetExec(atualizado);
      notify("success", `Ocorrência #${r.idOcorrencia} criada.`);
    } catch (e) { notify("danger", e.message); }
  };

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: "bi-speedometer2" },
    { id: "modelos", label: "Modelos", icon: "bi-ui-checks" },
    { id: "agendamentos", label: "Agendamentos", icon: "bi-calendar-event" },
    { id: "tarefas", label: "Tarefas hoje", icon: "bi-list-task" },
    { id: "execucoes", label: "Execuções", icon: "bi-journal-check" },
  ];

  return (
    <div className="container-fluid py-3">
      {banner && <Banner tipo={banner.type} onClose={() => setBanner(null)}>{banner.msg}</Banner>}

      <div className="page-header mb-3">
        <div>
          <h5 className="page-title mb-0">
            <i className="bi bi-list-check me-2 text-primary" style={{ fontSize: 22 }} />
            Checklists
          </h5>
        </div>
      </div>

      <ul className="nav nav-tabs mb-3">
        {TABS.map((t) => (
          <li className="nav-item" key={t.id}>
            <button className={`nav-link ${aba === t.id ? "active" : ""}`} onClick={() => setAba(t.id)}>
              <i className={`bi ${t.icon} me-1`} />{t.label}
            </button>
          </li>
        ))}
      </ul>

      {(aba === "modelos" || aba === "tarefas" || aba === "agendamentos") && (
        <div
          className="d-flex flex-wrap align-items-center gap-2 mb-3 px-1"
          style={{
            padding: "10px 12px",
            background: "var(--gray-50, #f8fafc)",
            borderRadius: 8,
            border: "1px solid var(--gray-100, #f1f5f9)",
          }}
        >
          <div className="d-flex flex-wrap gap-2 align-items-center flex-grow-1">
            {aba === "modelos" && (
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Buscar nome, descrição ou categoria..."
                value={buscaModelo}
                onChange={(e) => setBuscaModelo(e.target.value)}
                style={{ minWidth: 260, maxWidth: 420 }}
              />
            )}
            {aba === "tarefas" && (
              <>
                <select
                  className="form-select form-select-sm"
                  value={filtroStatusTarefa}
                  onChange={(e) => setFiltroStatusTarefa(e.target.value)}
                  style={{ width: "auto", minWidth: 160 }}
                >
                  <option value="">Todos os status</option>
                  <option value="Pendente">Pendente</option>
                  <option value="EmAndamento">Em andamento</option>
                  <option value="Atrasada">Atrasada</option>
                  <option value="Concluida">Concluída</option>
                  <option value="Cancelada">Cancelada</option>
                </select>
                <select
                  className="form-select form-select-sm"
                  value={filtroResponsavelTarefa}
                  onChange={(e) => setFiltroResponsavelTarefa(e.target.value)}
                  style={{ width: "auto", minWidth: 180 }}
                >
                  <option value="">Todos os responsáveis</option>
                  <option value="sem">Sem responsável</option>
                  {usuarios.map((u) => (
                    <option key={u.id} value={String(u.id)}>{u.nome}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div className="d-flex gap-2 ms-auto flex-shrink-0">
            {aba === "tarefas" && canEdit && (
              <button className="btn btn-outline-primary btn-sm" onClick={async () => {
                try {
                  await checklistService.gerarTarefas({ data: today() });
                  notify("success", "Tarefas geradas.");
                  carregar();
                } catch (e) { notify("danger", e.message); }
              }}>
                <i className="bi bi-arrow-repeat me-1" />Gerar tarefas do dia
              </button>
            )}
            {aba === "modelos" && canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => abrirModelo(null)}>
                <i className="bi bi-plus-lg me-1" />Novo modelo
              </button>
            )}
            {aba === "agendamentos" && canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => abrirAg(null)}>
                <i className="bi bi-plus-lg me-1" />Novo agendamento
              </button>
            )}
          </div>
        </div>
      )}

      {loading && <p className="text-muted">Carregando...</p>}

      {!loading && aba === "dashboard" && (
        <div className="row g-3">
          {[
            { l: "Pendentes hoje", v: kpi.pendentes, c: "warning" },
            { l: "Concluídas hoje", v: kpi.concluidas, c: "success" },
            { l: "Atrasadas", v: kpi.atrasadas, c: "danger" },
            { l: "NC hoje", v: kpi.totalnc, c: "secondary" },
          ].map((k) => (
            <div className="col-sm-6 col-lg-3" key={k.l}>
              <div className={`card border-0 shadow-sm border-start border-4 border-${k.c}`}>
                <div className="card-body">
                  <div className="text-muted small">{k.l}</div>
                  <div className="fs-3 fw-bold">{k.v ?? 0}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && aba === "modelos" && (
        <div className="table-responsive card border-0 shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr><th>Nome</th><th>Descrição</th><th>Categoria</th><th>Itens</th><th>Ativo</th><th /></tr>
            </thead>
            <tbody>
              {modelos.map((m) => (
                <tr key={m.id}>
                  <td className="fw-semibold">{m.nmmodelo}</td>
                  <td className="text-muted small" style={{ maxWidth: 280 }}>
                    {m.descricao ? (m.descricao.length > 80 ? `${m.descricao.slice(0, 80)}…` : m.descricao) : "—"}
                  </td>
                  <td>{m.categoria}</td>
                  <td>{m.qtditens}</td>
                  <td>{m.ativo ? "Sim" : "Não"}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => abrirModelo(m.id)}>Editar</button>
                  </td>
                </tr>
              ))}
              {!modelos.length && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    {buscaModeloDebounced ? "Nenhum modelo encontrado para a busca." : "Nenhum modelo."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && aba === "agendamentos" && (
        <div className="table-responsive card border-0 shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr><th>Rotina</th><th>Modelo</th><th>Recorrência</th><th>Horário</th><th>Responsável</th><th /></tr>
            </thead>
            <tbody>
              {agendamentos.map((a) => (
                <tr key={a.id}>
                  <td>{a.nmrotina}</td>
                  <td>{a.nmmodelo}</td>
                  <td>{a.recorrencia}</td>
                  <td>{String(a.hrinicio).slice(0, 5)} – {String(a.hrfim).slice(0, 5)}</td>
                  <td>{a.nmresponsavel || "—"}</td>
                  <td className="text-end">
                    {canEdit && <button className="btn btn-sm btn-outline-primary" onClick={() => abrirAg(a)}>Editar</button>}
                  </td>
                </tr>
              ))}
              {!agendamentos.length && <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum agendamento.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!loading && aba === "tarefas" && (
        <div className="table-responsive card border-0 shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr><th>Rotina</th><th>Local</th><th>Prazo</th><th>Responsável</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {tarefas.map((t) => (
                <tr key={t.id}>
                  <td>{t.nmrotina}</td>
                  <td>{t.nmlocal || "—"}</td>
                  <td>{fmtDT(t.dtlimite)}</td>
                  <td>{t.nmresponsavel || "—"}</td>
                  <td><span className={`badge bg-${ST_BADGE[t.status] || "secondary"}`}>{t.status}</span></td>
                  <td className="text-end">
                    {canEdit && t.status !== "Concluida" && (
                      <button className="btn btn-sm btn-outline-danger" onClick={async () => {
                        if (!window.confirm("Cancelar tarefa?")) return;
                        await checklistService.cancelarTarefa(t.id);
                        carregar();
                      }}>Cancelar</button>
                    )}
                  </td>
                </tr>
              ))}
              {!tarefas.length && <tr><td colSpan={6} className="text-center text-muted py-4">Nenhuma tarefa hoje.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!loading && aba === "execucoes" && (
        <div className="table-responsive card border-0 shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr><th>Início</th><th>Fim</th><th>Rotina</th><th>Executor</th><th>Duração</th><th>NC</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {execucoes.map((e) => (
                <tr key={e.id}>
                  <td className="small">{fmtDT(e.dtinicio)}</td>
                  <td className="small">{e.dtfim ? fmtDT(e.dtfim) : "—"}</td>
                  <td>{e.nmrotina}</td>
                  <td>{e.nmusuario || "—"}</td>
                  <td className="small">{duracaoExecucao(e)}</td>
                  <td>{e.qtd_nc || 0}</td>
                  <td>{e.status}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => verExec(e.id)}>Detalhe</button>
                  </td>
                </tr>
              ))}
              {!execucoes.length && <tr><td colSpan={8} className="text-center text-muted py-4">Nenhuma execução.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Modelo */}
      {modalMod && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalMod === "new" ? "Novo modelo" : "Editar modelo"}</h5>
                <button className="btn-close" onClick={() => setModalMod(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Nome</label>
                    <input className="form-control" value={formMod.nmmodelo} onChange={(e) => setFormMod({ ...formMod, nmmodelo: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Categoria</label>
                    <select className="form-select" value={formMod.categoria} onChange={(e) => setFormMod({ ...formMod, categoria: e.target.value })}>
                      {(meta.categorias || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" checked={!!formMod.ativo} onChange={(e) => setFormMod({ ...formMod, ativo: e.target.checked })} id="modAtivo" />
                      <label className="form-check-label" htmlFor="modAtivo">Ativo</label>
                    </div>
                  </div>
                </div>
                <h6>Itens</h6>
                {formMod.itens.map((it, idx) => (
                  <div className="border rounded p-2 mb-2" key={idx}>
                    <div className="row g-2">
                      <div className="col-md-5">
                        <input className="form-control form-control-sm" placeholder="Pergunta" value={it.pergunta}
                          onChange={(e) => { const itens = [...formMod.itens]; itens[idx] = { ...it, pergunta: e.target.value }; setFormMod({ ...formMod, itens }); }} />
                      </div>
                      <div className="col-md-3">
                        <select className="form-select form-select-sm" value={it.tpresposta}
                          onChange={(e) => { const itens = [...formMod.itens]; itens[idx] = { ...it, tpresposta: e.target.value }; setFormMod({ ...formMod, itens }); }}>
                          {(meta.tpResposta || []).map((t) => <option key={t} value={t}>{TP_LABEL[t] || t}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <input className="form-control form-control-sm" placeholder="Opções (;)" value={it.opcoes || ""}
                          onChange={(e) => { const itens = [...formMod.itens]; itens[idx] = { ...it, opcoes: e.target.value }; setFormMod({ ...formMod, itens }); }} />
                      </div>
                      <div className="col-md-1">
                        <button className="btn btn-sm btn-outline-danger w-100" onClick={() => setFormMod({ ...formMod, itens: formMod.itens.filter((_, i) => i !== idx) })}>×</button>
                      </div>
                      <div className="col-12 d-flex gap-3">
                        <div className="form-check form-check-inline">
                          <input className="form-check-input" type="checkbox" checked={!!it.obrigatorio}
                            onChange={(e) => { const itens = [...formMod.itens]; itens[idx] = { ...it, obrigatorio: e.target.checked }; setFormMod({ ...formMod, itens }); }} id={`ob${idx}`} />
                          <label className="form-check-label" htmlFor={`ob${idx}`}>Obrigatório</label>
                        </div>
                        {it.tpresposta === "SimNao" && (
                          <div className="form-check form-check-inline">
                            <input className="form-check-input" type="checkbox" checked={!!it.geranc}
                              onChange={(e) => { const itens = [...formMod.itens]; itens[idx] = { ...it, geranc: e.target.checked }; setFormMod({ ...formMod, itens }); }} id={`nc${idx}`} />
                            <label className="form-check-label" htmlFor={`nc${idx}`}>NC se Não</label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setFormMod({ ...formMod, itens: [...formMod.itens, { ...ITEM_EMPTY }] })}>
                  + Item
                </button>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalMod(null)}>Fechar</button>
                {canEdit && <button className="btn btn-primary" onClick={salvarModelo}>Salvar</button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Agendamento */}
      {modalAg && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalAg === "new" ? "Novo agendamento" : "Editar agendamento"}</h5>
                <button className="btn-close" onClick={() => setModalAg(null)} />
              </div>
              <div className="modal-body row g-3">
                <div className="col-md-6">
                  <label className="form-label">Nome da rotina</label>
                  <input className="form-control" value={formAg.nmrotina} onChange={(e) => setFormAg({ ...formAg, nmrotina: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Modelo</label>
                  <select className="form-select" value={formAg.idmodelo} onChange={(e) => setFormAg({ ...formAg, idmodelo: e.target.value })}>
                    <option value="">—</option>
                    {modelos.map((m) => <option key={m.id} value={m.id}>{m.nmmodelo}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Recorrência</label>
                  <select className="form-select" value={formAg.recorrencia} onChange={(e) => setFormAg({ ...formAg, recorrencia: e.target.value })}>
                    <option value="Diaria">Diária</option>
                    <option value="Semanal">Semanal</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Início</label>
                  <input type="time" className="form-control" value={String(formAg.hrinicio).slice(0, 5)} onChange={(e) => setFormAg({ ...formAg, hrinicio: e.target.value })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Fim</label>
                  <input type="time" className="form-control" value={String(formAg.hrfim).slice(0, 5)} onChange={(e) => setFormAg({ ...formAg, hrfim: e.target.value })} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Responsável</label>
                  <select className="form-select" value={formAg.idresponsavel} onChange={(e) => setFormAg({ ...formAg, idresponsavel: e.target.value })}>
                    <option value="">—</option>
                    {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome} ({u.perfil})</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Local / área</label>
                  <input className="form-control" value={formAg.nmlocal || ""} onChange={(e) => setFormAg({ ...formAg, nmlocal: e.target.value })} />
                </div>
                {formAg.recorrencia === "Semanal" && (
                  <div className="col-12">
                    <label className="form-label">Dias (1=Seg … 7=Dom, separados por vírgula)</label>
                    <input className="form-control" value={formAg.diasemana || ""} onChange={(e) => setFormAg({ ...formAg, diasemana: e.target.value })} />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setModalAg(null)}>Fechar</button>
                {canEdit && <button className="btn btn-primary" onClick={salvarAg}>Salvar</button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detalhe execução */}
      {detExec && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.4)" }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{detExec.execucao?.nmrotina} — {detExec.execucao?.nmmodelo}</h5>
                <div className="d-flex align-items-center gap-2">
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={imprimirDetalhe}>
                    <i className="bi bi-printer me-1" />Imprimir / PDF
                  </button>
                  <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setDetExec(null)} />
                </div>
              </div>
              <div className="modal-body">
                <div className="row g-2 mb-3">
                  <div className="col-md-4">
                    <div className="border rounded p-2 h-100 bg-light">
                      <div className="small text-muted">Executor</div>
                      <div className="fw-medium">{detExec.execucao?.nmusuario || "—"}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-2 h-100 bg-light">
                      <div className="small text-muted">Início</div>
                      <div className="fw-medium">{fmtDTFull(detExec.execucao?.dtinicio)}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-2 h-100 bg-light">
                      <div className="small text-muted">Finalização</div>
                      <div className="fw-medium">
                        {detExec.execucao?.dtfim ? fmtDTFull(detExec.execucao.dtfim) : "Em andamento"}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-2 h-100">
                      <div className="small text-muted">Duração</div>
                      <div className="fw-medium">{duracaoExecucao(detExec.execucao)}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-2 h-100">
                      <div className="small text-muted">Status</div>
                      <div className="fw-medium">{detExec.execucao?.status || "—"}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-2 h-100">
                      <div className="small text-muted">Não conformidades</div>
                      <div className="fw-medium">{detExec.execucao?.qtd_nc || 0}</div>
                    </div>
                  </div>
                  {detExec.execucao?.nmlocal && (
                    <div className="col-12">
                      <div className="small text-muted"><i className="bi bi-geo-alt me-1" />{detExec.execucao.nmlocal}</div>
                    </div>
                  )}
                </div>
                <table className="table table-sm">
                  <thead><tr><th>Pergunta</th><th>Resposta</th><th>NC</th><th className="text-end">Ações</th></tr></thead>
                  <tbody>
                    {(detExec.respostas || []).map((r) => {
                      const fotos = parseFotos(r.fotos);
                      return (
                      <tr key={r.id} className={r.conforme === 0 ? "table-danger" : ""}>
                        <td>{r.pergunta}</td>
                        <td>
                          {r.tpresposta === "Foto" && fotos.length
                            ? <span className="text-muted">{fotos.length} foto(s)</span>
                            : (r.resposta || "—")}
                          {r.comentario ? <div className="small text-muted">{r.comentario}</div> : null}
                        </td>
                        <td>{r.conforme === 0 ? "Sim" : "—"}</td>
                        <td className="text-end">
                          <div className="d-flex flex-column align-items-end gap-1">
                            {fotos.length > 0 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setFotosViewer({
                                  titulo: r.pergunta,
                                  urls: fotos.map(urlFoto),
                                })}
                              >
                                <i className="bi bi-images me-1" />
                                Ver fotos ({fotos.length})
                              </button>
                            )}
                            {r.conforme === 0 && !r.idocorrencia && canEdit && (
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => abrirOcorrencia(r.id)}>
                                Abrir ocorrência
                              </button>
                            )}
                            {r.idocorrencia ? <span className="small text-success">Ocorrência #{r.idocorrencia}</span> : null}
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>

                <hr />
                <h6 className="mb-2">Planos de ação (5W2H)</h6>
                <div className="row g-2 mb-3">
                  <div className="col-md-4">
                    <label className="form-label form-label-sm">Vincular item NC</label>
                    <select className="form-select form-select-sm" value={formPA.idResposta} onChange={(e) => setFormPA({ ...formPA, idResposta: e.target.value })}>
                      <option value="">— opcional —</option>
                      {(detExec.respostas || []).filter((r) => r.conforme === 0).map((r) => (
                        <option key={r.id} value={r.id}>{r.pergunta.slice(0, 80)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label form-label-sm">O que</label>
                    <input className="form-control form-control-sm" value={formPA.oque} onChange={(e) => setFormPA({ ...formPA, oque: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-sm">Quem</label>
                    <select className="form-select form-select-sm" value={formPA.quemIdUsuario} onChange={(e) => setFormPA({ ...formPA, quemIdUsuario: e.target.value })}>
                      <option value="">—</option>
                      {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-sm">Quando</label>
                    <input type="date" className="form-control form-control-sm" value={formPA.quandoData} onChange={(e) => setFormPA({ ...formPA, quandoData: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-sm">Prioridade</label>
                    <select className="form-select form-select-sm" value={formPA.prioridade} onChange={(e) => setFormPA({ ...formPA, prioridade: e.target.value })}>
                      <option value="Baixa">Baixa</option>
                      <option value="Media">Média</option>
                      <option value="Alta">Alta</option>
                      <option value="Urgente">Urgente</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-sm">Por que</label>
                    <input className="form-control form-control-sm" value={formPA.porque} onChange={(e) => setFormPA({ ...formPA, porque: e.target.value })} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-sm">Como</label>
                    <input className="form-control form-control-sm" value={formPA.como} onChange={(e) => setFormPA({ ...formPA, como: e.target.value })} />
                  </div>
                </div>
                {canEdit && <button className="btn btn-sm btn-primary mb-3" onClick={criarPlanoAcao}>Adicionar plano de ação</button>}

                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead><tr><th>O que</th><th>Quem</th><th>Quando</th><th>Status</th></tr></thead>
                    <tbody>
                      {(detExec.planosAcao || []).map((pa) => (
                        <tr key={pa.id}>
                          <td>{pa.oque}</td>
                          <td>{pa.nmquem || "—"}</td>
                          <td>{fmtD(pa.quando_data)}</td>
                          <td>{pa.status}</td>
                        </tr>
                      ))}
                      {!(detExec.planosAcao || []).length && <tr><td colSpan={4} className="text-muted text-center">Sem planos de ação.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDetExec(null)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Visualizador de fotos (execução) */}
      {fotosViewer && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,.6)", zIndex: 1060 }} role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate" style={{ maxWidth: "90%" }}>
                  <i className="bi bi-images me-2" />
                  {fotosViewer.titulo}
                </h5>
                <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setFotosViewer(null)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {fotosViewer.urls.map((url, i) => (
                    <div key={url} className="col-12 col-sm-6">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="d-block">
                        <img
                          src={url}
                          alt={`Foto ${i + 1}`}
                          className="img-fluid rounded border w-100"
                          style={{ maxHeight: 360, objectFit: "contain", background: "#f8f9fa" }}
                        />
                      </a>
                      <div className="small text-muted text-center mt-1">
                        Foto {i + 1} de {fotosViewer.urls.length} — clique para abrir em tamanho real
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setFotosViewer(null)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
